import { ensureFreshAccess } from '../api/client';
import type { AutoChargeEvent, NavStuck, UiMessage } from '../api/types';
import { isLegacy, queryToken, useAuth } from '../auth/store';
import { useLive } from './store';
import { onNavStuck } from '../lib/stuck';
import { onAutoCharge } from '../lib/chargeEvents';
import { parseChargers } from '../lib/chargerState';

type Listener = (m: UiMessage) => void;

/**
 * `/ws/ui/?token=` (PROTOCOL §7) with reconnect + exponential backoff (1 s → 15 s).
 * One instance per signed-in session; `start()`/`stop()` follow the auth state.
 */
class UiSocket {
  private ws: WebSocket | null = null;
  private backoff = 1000;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private running = false;
  private gen = 0;
  private listeners = new Set<Listener>();
  private openListeners = new Set<() => void>();

  start() {
    if (this.running) return;
    this.running = true;
    this.gen++;
    void this.connect(this.gen);
  }

  stop() {
    this.running = false;
    this.gen++;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    const ws = this.ws;
    this.ws = null;
    ws?.close();
    useLive.getState().setWsState('closed');
  }

  get open() {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  send(obj: unknown): boolean {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(obj));
      return true;
    }
    return false;
  }

  on(fn: Listener) {
    this.listeners.add(fn);
    return () => void this.listeners.delete(fn);
  }

  onOpen(fn: () => void) {
    this.openListeners.add(fn);
    return () => void this.openListeners.delete(fn);
  }

  private async connect(gen: number) {
    if (!this.running || gen !== this.gen) return;
    useLive.getState().setWsState('connecting');
    await ensureFreshAccess().catch(() => undefined);
    const auth = useAuth.getState().auth;
    if (!auth || !this.running || gen !== this.gen) return;
    this.ws?.close();
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const path = isLegacy(auth) ? '/ws/ui' : '/ws/ui/';
    const ws = new WebSocket(`${proto}://${location.host}${path}?token=${encodeURIComponent(queryToken(auth))}`);
    this.ws = ws;
    ws.onopen = () => {
      if (this.ws !== ws) return;
      this.backoff = 1000;
      useLive.getState().setWsState('open');
      for (const fn of this.openListeners) fn();
    };
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      useLive.getState().setWsState('closed');
      for (const fn of this.listeners) fn({ type: 'media', serial: '*', error: 'socket_closed' });
      if (!this.running) return;
      this.timer = setTimeout(() => void this.connect(gen), this.backoff);
      this.backoff = Math.min(15000, this.backoff * 2);
    };
    ws.onmessage = (ev) => {
      if (this.ws !== ws) return;
      let m: UiMessage;
      try {
        m = JSON.parse(String(ev.data)) as UiMessage;
      } catch {
        return;
      }
      dispatch(m);
      for (const fn of this.listeners) fn(m);
    };
  }
}

function dispatch(m: UiMessage) {
  const live = useLive.getState();
  switch (m.type) {
    case 'robots':
      live.setRobots(m.robots);
      break;
    case 'robotStatus':
      live.setStatus(m.serial, m.online, m.status);
      break;
    case 'robotEvent': {
      const d = m.data ?? {};
      if (m.event === 'heard') {
        const h = d as { text?: string; lang?: string; engine?: string };
        live.setHeard(m.serial, { text: h.text ?? '', lang: h.lang, engine: h.engine });
        live.addLog(m.serial, 'heard', 'heard', `“${h.text ?? ''}” · ${h.engine ?? '?'} · ${h.lang ?? '?'}`);
      } else if (m.event === 'autoCharge' && typeof d.phase === 'string') {
        onAutoCharge(m.serial, d as unknown as AutoChargeEvent);
      } else if (m.event === 'navStuck' && typeof d.phase === 'string') {
        onNavStuck(m.serial, d as unknown as NavStuck);
      } else {
        const text = Object.keys(d).length ? summarize(d) : '';
        live.addLog(m.serial, 'event', m.event, text);
      }
      break;
    }
    case 'agent':
      live.addAgent(m);
      break;
    case 'media-lib':
      live.setMediaLib(m);
      break;
    case 'cmdLog': {
      if (m.row && typeof m.row === 'object') live.addCmdLog(m.serial, m.row);
      break;
    }
    case 'sensor': {
      // the robot's own `sensor` event is already in the log (robotEvent); only remember when it was seen
      if (typeof m.serial === 'string') live.noteSensor(m.serial, String(m.kind));
      break;
    }
    case 'chargers': {
      if (typeof m.mapId === 'string') live.setChargers(m.mapId, parseChargers(m.chargers ?? m));
      break;
    }
    case 'autoCharge': {
      const { type: _t, serial, ...ev } = m;
      onAutoCharge(serial, ev);
      break;
    }
    case 'navStuck': {
      const { type: _t, serial, ...ev } = m;
      onNavStuck(serial, ev);
      break;
    }
    case 'autonomy':
      live.addAutonomy(m);
      break;
    case 'schedule': {
      const { type: _t, id, event, ...data } = m;
      live.setScheduleEvent(id, String(event), data);
      break;
    }
  }
}

function summarize(d: Record<string, unknown>): string {
  if (typeof d.message === 'string') return d.message;
  if (typeof d.name === 'string') return d.name;
  const s = JSON.stringify(d);
  return s.length > 160 ? `${s.slice(0, 157)}…` : s;
}

export const uiSocket = new UiSocket();
